import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { exportTimelinePdf } from "../lib/exportTimeline";
import { formatDate } from "../lib/formatDate";
import { daysBetween, elapsedVsPromised, formatDayCount } from "../lib/dateMath";
import { displayFactTitle, factMeta } from "../lib/factMeta";
import { describeRecord } from "../lib/recordMeta";
import FactCard from "./ui/FactCard";
import Tooltip from "./ui/Tooltip";
import type { TimelineSubView } from "../lib/viewModeStore";
import { SIGNIFICANCE_LABELS, SOURCE_TYPE_LABELS } from "../types";
import type {
  Case,
  CaseRecord,
  RecordFact,
  TimelineRecord,
} from "../types";
import "./TimelineTab.css";

// The fact-based Timeline tab (Phase 2/3). No AI, no generation step — a
// single deterministic read of records + verified facts drives both sub-views.
//
// By default the timeline stays focused on what HAPPENED (case_event facts).
// Two off-by-default toggles reveal the policy/context layers:
//   - "Show coverage terms": coverage_term facts (deductibles, caps, rates)
//   - "Show background": background facts (and background-only records)
// Both are rendered with a distinct visual tag so they read apart from events.

interface Props {
  caseData: Case;
  records: CaseRecord[];
  onNavigateToRecord?: (recordId: string) => void;
  subView: TimelineSubView;
  onSubViewChange: (v: TimelineSubView) => void;
}

interface DateGroup {
  date: string;
  items: TimelineRecord[];
}

// Deterministic "how long ago" label for a date-group heading. UTC day math
// (dateMath), never an AI restatement. "" when undeterminable.
function relativeDayLabel(dateISO: string, nowISO: string): string {
  const days = daysBetween(dateISO, nowISO);
  if (days === null) return "";
  if (days === 0) return "today";
  if (days > 0) return `${formatDayCount(days)} ago`;
  return `in ${formatDayCount(days)}`;
}

// Distinct treatment for non-event facts shown via a toggle.
function factTag(
  category: string | undefined,
): { label: string; cls: string } | null {
  if (category === "coverage_term") return { label: "Coverage term", cls: "coverage" };
  if (category === "background") return { label: "Background", cls: "background" };
  return null;
}

export default function TimelineTab({
  caseData,
  records,
  onNavigateToRecord,
  subView,
  onSubViewChange,
}: Props) {
  const [timeline, setTimeline] = useState<TimelineRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [viewFilter, setViewFilter] = useState<"all" | "key">("all");
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [showBackground, setShowBackground] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);

  const recordById = useMemo(() => {
    const m = new Map<string, CaseRecord>();
    for (const r of records) m.set(r.id, r);
    return m;
  }, [records]);

  // Single "now" for every deterministic duration this render computes, so
  // the day counts on the cards and in the summary agree.
  const nowISO = useMemo(() => new Date().toISOString(), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api.getTimeline(caseData.id);
        if (!cancelled) setTimeline(r.timeline);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseData.id]);

  const all = timeline ?? [];
  const totalFacts = useMemo(
    () => all.reduce((n, r) => n + r.facts.length, 0),
    [all],
  );

  // Which facts appear inline on a card under the current toggles. case_event
  // and valuation facts always show; coverage_term and background are gated.
  const factVisibleInline = (f: RecordFact) => {
    if (f.fact_category === "background") return showBackground;
    if (f.fact_category === "coverage_term") return showCoverage;
    return true;
  };

  const inlineFacts = useMemo(() => {
    const map = new Map<string, RecordFact[]>();
    for (const rec of all) map.set(rec.id, rec.facts.filter(factVisibleInline));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, showBackground, showCoverage]);

  const availableTypes = useMemo(() => {
    const s = new Set<string>();
    for (const rec of all)
      for (const f of inlineFacts.get(rec.id) ?? []) s.add(f.fact_type);
    return [...s];
  }, [all, inlineFacts]);

  const keyCount = useMemo(
    () => all.filter((r) => r.significance === "high").length,
    [all],
  );

  const shown = useMemo(() => {
    return all.filter((rec) => {
      if (viewFilter === "key" && rec.significance !== "high") return false;
      const inline = inlineFacts.get(rec.id) ?? [];
      if (typeFilter.size > 0 && !inline.some((f) => typeFilter.has(f.fact_type)))
        return false;
      // Records with no extracted facts are real case events (photo sets,
      // empty records) — always show them as subject-only cards.
      if (rec.facts.length === 0) return true;
      // Hide records whose ONLY facts are background, unless the user opts in.
      // A record with any case_event / coverage_term / valuation fact stays.
      const allBackground = rec.facts.every(
        (f) => f.fact_category === "background",
      );
      if (!showBackground && allBackground) return false;
      return true;
    });
  }, [all, viewFilter, typeFilter, inlineFacts, showBackground]);

  const groups = useMemo<DateGroup[]>(() => {
    const out: DateGroup[] = [];
    for (const rec of shown) {
      const last = out[out.length - 1];
      // Group by calendar day — record_date carries a time component.
      if (last && last.date.slice(0, 10) === rec.date.slice(0, 10))
        last.items.push(rec);
      else out.push({ date: rec.date, items: [rec] });
    }
    return out;
  }, [shown]);

  function toggleType(t: string) {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  function typeMeta(rec: TimelineRecord): { emoji: string; label: string } {
    const full = recordById.get(rec.id);
    if (full) {
      const d = describeRecord(full);
      return { emoji: d.typeEmoji, label: d.typeLabel };
    }
    return {
      emoji: "📄",
      label: (SOURCE_TYPE_LABELS as Record<string, string>)[rec.type] ?? "Record",
    };
  }

  async function exportPdf() {
    if (!timeline) return;
    setExporting(true);
    try {
      await exportTimelinePdf({
        caseData,
        records: shown,
        generatedAt: new Date().toISOString(),
        includeCoverage: showCoverage,
        includeBackground: showBackground,
      });
    } catch (err) {
      setError(`PDF export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="timeline-tab-loading">
        <span className="spinner" /> Loading timeline…
      </div>
    );
  }
  if (error) return <div className="form-error">{error}</div>;
  if (!timeline || timeline.length === 0) {
    return (
      <div className="timeline-tab-empty">
        <h2>No records yet</h2>
        <p>Add records to this case to build a timeline.</p>
      </div>
    );
  }

  return (
    <div className="timeline-tab">
      <div className="timeline-tab-header">
        <div
          className="timeline-subview-switcher"
          role="radiogroup"
          aria-label="Timeline view"
        >
          <button
            type="button"
            role="radio"
            aria-checked={subView === "visual"}
            className={`timeline-subview-btn${subView === "visual" ? " active" : ""}`}
            onClick={() => onSubViewChange("visual")}
          >
            Visual
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={subView === "written"}
            className={`timeline-subview-btn${subView === "written" ? " active" : ""}`}
            onClick={() => onSubViewChange("written")}
          >
            Written
          </button>
        </div>

        <span className="muted timeline-tab-counts">
          {shown.length} record{shown.length === 1 ? "" : "s"} · {totalFacts}{" "}
          verified fact{totalFacts === 1 ? "" : "s"}
        </span>

        <div className="timeline-tab-actions">
          <Tooltip title="Download the timeline as a PDF (records + verified quotes)">
            <button
              type="button"
              className="btn-primary"
              onClick={() => void exportPdf()}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export as PDF"}
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="timeline-filters">
        <div className="timeline-filter-group" role="group" aria-label="Significance filter">
          <button
            type="button"
            className={viewFilter === "all" ? "active" : ""}
            onClick={() => setViewFilter("all")}
            aria-pressed={viewFilter === "all"}
          >
            Show all
          </button>
          <button
            type="button"
            className={viewFilter === "key" ? "active" : ""}
            onClick={() => setViewFilter("key")}
            aria-pressed={viewFilter === "key"}
            disabled={keyCount === 0}
          >
            Key events only ({keyCount})
          </button>
        </div>

        {availableTypes.length > 0 && (
          <div className="timeline-type-chips" role="group" aria-label="Fact type filter">
            {availableTypes.map((t) => {
              const meta = factMeta(t);
              const active = typeFilter.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  className={`fact-type-chip${active ? " active" : ""}`}
                  style={active ? { borderColor: meta.color, color: meta.color } : undefined}
                  onClick={() => toggleType(t)}
                  aria-pressed={active}
                >
                  {meta.emoji} {meta.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="timeline-context-toggles">
          <label className="timeline-bg-toggle">
            <input
              type="checkbox"
              checked={showCoverage}
              onChange={(e) => setShowCoverage(e.target.checked)}
            />
            Show coverage terms
          </label>
          <label className="timeline-bg-toggle">
            <input
              type="checkbox"
              checked={showBackground}
              onChange={(e) => setShowBackground(e.target.checked)}
            />
            Show background
          </label>
        </div>
      </div>

      {totalFacts === 0 && (
        <div className="timeline-empty-banner" role="status">
          Extract facts to populate the timeline with verified source-linked
          events. Click <strong>Extract all facts</strong> on the Overview tab.
        </div>
      )}

      {shown.length > 0 && (
        <DurationsSummary
          records={shown}
          nowISO={nowISO}
          onNavigateToRecord={onNavigateToRecord}
        />
      )}

      {subView === "visual" ? (
        <VisualTimeline
          groups={groups}
          inlineFacts={inlineFacts}
          recordById={recordById}
          typeMeta={typeMeta}
          nowISO={nowISO}
          onNavigateToRecord={onNavigateToRecord}
        />
      ) : (
        <WrittenTimeline
          groups={groups}
          inlineFacts={inlineFacts}
          typeMeta={typeMeta}
          nowISO={nowISO}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visual — one card per record, grouped by date.
// ---------------------------------------------------------------------------

function VisualTimeline({
  groups,
  inlineFacts,
  recordById,
  typeMeta,
  nowISO,
  onNavigateToRecord,
}: {
  groups: DateGroup[];
  inlineFacts: Map<string, RecordFact[]>;
  recordById: Map<string, CaseRecord>;
  typeMeta: (rec: TimelineRecord) => { emoji: string; label: string };
  nowISO: string;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  if (groups.length === 0) {
    return <div className="timeline-none muted">No records match the current filters.</div>;
  }
  return (
    <div className="timeline-visual2">
      {groups.map((g) => (
        <section key={g.date} className="timeline-date-group">
          <h3 className="timeline-date-heading">
            {formatDate(g.date, "long") || g.date}
            <span className="timeline-elapsed">{relativeDayLabel(g.date, nowISO)}</span>
          </h3>
          <ol className="timeline-cards">
            {g.items.map((rec) => {
              const meta = typeMeta(rec);
              const facts = inlineFacts.get(rec.id) ?? [];
              // Prefer the deterministic content label; keep the original
              // subject as the tooltip.
              const label = recordById.get(rec.id)?.content_label?.trim();
              const subj = rec.subject?.trim();
              return (
                <li
                  key={rec.id}
                  className={`timeline-card sig-${rec.significance} dir-${rec.direction}`}
                >
                  <span className="timeline-card-dot" aria-hidden />
                  <div className="timeline-card-body">
                    <div
                      className="timeline-card-subject"
                      title={label && subj ? subj : undefined}
                    >
                      {label || subj || "(no subject)"}
                    </div>
                    {(rec.from || rec.to) && (
                      <div className="timeline-card-parties">
                        {rec.from ?? "?"}
                        {rec.to ? ` → ${rec.to}` : ""}
                      </div>
                    )}
                    <div className="timeline-card-badges">
                      <span className="timeline-card-type">
                        {meta.emoji} {meta.label}
                      </span>
                      {rec.significance === "high" && (
                        <span className="timeline-card-key">
                          {SIGNIFICANCE_LABELS.high}
                        </span>
                      )}
                      {rec.content_trust === "low" && (
                        <span
                          className="timeline-card-trust"
                          title="Extracted from an audio transcription"
                        >
                          ⚠ Transcription
                        </span>
                      )}
                    </div>

                    {facts.length > 0 && (
                      <div className="timeline-card-facts">
                        {facts.map((f) => {
                          const tag = factTag(f.fact_category);
                          return (
                            <div
                              key={f.id}
                              className={`timeline-fact${tag ? ` tl-${tag.cls}` : ""}`}
                            >
                              {tag && (
                                <span className="timeline-fact-tag">{tag.label}</span>
                              )}
                              <FactCard
                                fact={f}
                                record={recordById.get(rec.id)}
                                hideSource
                                onNavigateToRecord={onNavigateToRecord}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="timeline-card-footer">
                      {rec.attachment_count > 0 && (
                        <span className="timeline-card-attach">
                          📎 {rec.attachment_count} attachment
                          {rec.attachment_count === 1 ? "" : "s"}
                        </span>
                      )}
                      {onNavigateToRecord && (
                        <button
                          type="button"
                          className="timeline-card-source"
                          onClick={() => onNavigateToRecord(rec.id)}
                        >
                          View source record →
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Written — mechanically assembled text. No AI prose: record metadata plus
// each fact's summary and verbatim quote.
// ---------------------------------------------------------------------------

function buildWrittenText(
  groups: DateGroup[],
  inlineFacts: Map<string, RecordFact[]>,
  typeMeta: (rec: TimelineRecord) => { label: string },
): string {
  const out: string[] = [];
  for (const g of groups) {
    out.push(formatDate(g.date, "long") || g.date);
    out.push("");
    for (const rec of g.items) {
      out.push(rec.subject?.trim() || "(no subject)");
      const parties = [rec.from, rec.to].filter(Boolean).join(" → ");
      out.push(`${parties ? parties + " " : ""}(${typeMeta(rec).label})`);
      for (const f of inlineFacts.get(rec.id) ?? []) {
        const tag = factTag(f.fact_category);
        out.push(
          `  • ${tag ? `[${tag.label}] ` : ""}${displayFactTitle(f)}: "${f.verbatim_quote}"`,
        );
      }
      out.push("");
    }
  }
  return out.join("\n").trim();
}

function WrittenTimeline({
  groups,
  inlineFacts,
  typeMeta,
  nowISO,
}: {
  groups: DateGroup[];
  inlineFacts: Map<string, RecordFact[]>;
  typeMeta: (rec: TimelineRecord) => { emoji: string; label: string };
  nowISO: string;
}) {
  const [copied, setCopied] = useState(false);

  async function copyText() {
    try {
      await navigator.clipboard.writeText(
        buildWrittenText(groups, inlineFacts, typeMeta),
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  if (groups.length === 0) {
    return <div className="timeline-none muted">No records match the current filters.</div>;
  }

  return (
    <div className="timeline-written2">
      <div className="timeline-written-actions">
        <button type="button" className="btn-subtle" onClick={() => void copyText()}>
          {copied ? "✓ Copied" : "Copy text"}
        </button>
      </div>
      {groups.map((g) => (
        <section key={g.date} className="timeline-written-group">
          <h3 className="timeline-date-heading">
            {formatDate(g.date, "long") || g.date}
            <span className="timeline-elapsed">{relativeDayLabel(g.date, nowISO)}</span>
          </h3>
          {g.items.map((rec) => {
            const facts = inlineFacts.get(rec.id) ?? [];
            const parties = [rec.from, rec.to].filter(Boolean).join(" → ");
            return (
              <div key={rec.id} className="timeline-written-record">
                <div className="timeline-written-subject">
                  {rec.subject?.trim() || "(no subject)"}
                </div>
                <div className="timeline-written-meta">
                  {parties ? `${parties} ` : ""}({typeMeta(rec).label})
                </div>
                {facts.length > 0 && (
                  <ul className="timeline-written-facts">
                    {facts.map((f) => {
                      const tag = factTag(f.fact_category);
                      return (
                        <li key={f.id}>
                          {tag && (
                            <span className={`timeline-written-tag tl-${tag.cls}`}>
                              {tag.label}
                            </span>
                          )}{" "}
                          <span className="timeline-written-fact-summary">
                            {displayFactTitle(f)}:
                          </span>{" "}
                          <span className="timeline-written-fact-quote">
                            “{f.verbatim_quote}”
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Durations — deterministic day math over the shown records (Round 12, Part 2).
// Case span, time since last activity, and each promised-deadline countdown.
// Every figure is an exact UTC day count (dateMath); no AI, no restatement.
// ---------------------------------------------------------------------------

interface DeadlineRow {
  recordId: string;
  summary: string;
  deadline: string;
  days: number;
  overdue: boolean;
  dueToday: boolean;
}

function DurationsSummary({
  records,
  nowISO,
  onNavigateToRecord,
}: {
  records: TimelineRecord[];
  nowISO: string;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const dated = records
    .map((r) => r.date)
    .filter((d): d is string => !!d)
    .sort();
  const first = dated[0] ?? null;
  const last = dated[dated.length - 1] ?? null;
  const span = first && last ? daysBetween(first, last) : null;
  const sinceLast = last ? daysBetween(last, nowISO) : null;

  const deadlines: DeadlineRow[] = [];
  for (const r of records) {
    for (const f of r.facts) {
      if (!f.deadline) continue;
      const e = elapsedVsPromised(f.deadline, nowISO);
      if (!e) continue;
      deadlines.push({
        recordId: r.id,
        summary: (f.summary || "Deadline").trim(),
        deadline: f.deadline.slice(0, 10),
        days: e.days,
        overdue: e.overdue,
        dueToday: e.dueToday,
      });
    }
  }
  deadlines.sort((a, b) => a.days - b.days); // most overdue first

  // Nothing worth showing if there's no span and no deadlines.
  if (span === null && deadlines.length === 0) return null;

  return (
    <section className="timeline-durations" aria-label="Computed durations">
      <h3 className="timeline-durations-title">
        Durations <span className="timeline-durations-tag">computed</span>
      </h3>
      <div className="timeline-durations-grid">
        {span !== null && (
          <div className="timeline-duration-stat">
            <div className="timeline-duration-value">{formatDayCount(span)}</div>
            <div className="timeline-duration-label">
              case span ({first!.slice(0, 10)} → {last!.slice(0, 10)})
            </div>
          </div>
        )}
        {sinceLast !== null && (
          <div className="timeline-duration-stat">
            <div className="timeline-duration-value">{formatDayCount(sinceLast)}</div>
            <div className="timeline-duration-label">
              since last activity ({last!.slice(0, 10)})
            </div>
          </div>
        )}
      </div>
      {deadlines.length > 0 && (
        <ul className="timeline-deadline-list">
          {deadlines.map((d, i) => (
            <li
              key={`${d.recordId}-${i}`}
              className={`timeline-deadline${d.overdue ? " overdue" : ""}`}
            >
              <span className="timeline-deadline-text">
                {d.summary} — promised {d.deadline}:{" "}
                <strong>
                  {d.dueToday
                    ? "due today"
                    : d.overdue
                      ? `${formatDayCount(d.days)} overdue`
                      : `${formatDayCount(d.days)} remaining`}
                </strong>
              </span>
              {onNavigateToRecord && (
                <button
                  type="button"
                  className="fact-source-link"
                  onClick={() => onNavigateToRecord(d.recordId)}
                >
                  source →
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
